import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Calendar, 
  Clock, 
  Target, 
  BookOpen, 
  Printer,
  FileText,
  PenTool,
  Lightbulb,
  Users,
  CheckCircle2,
  Home
} from 'lucide-react';
import { primaryCurriculumData, getSchemeOfWork } from '../../data/primaryCurriculumData';

const PrimaryLessonPlanGenerator = ({ defaultYear = 1, defaultSubject = 'english', allowedYears = null }) => {
  // Use allowed years or default to all primary years
  const yearOptions = allowedYears && allowedYears.length > 0 
    ? allowedYears.map(y => typeof y === 'string' ? parseInt(y.replace('Year ', '')) : y)
    : [1, 2, 3, 4, 5, 6];
  
  const [selectedYear, setSelectedYear] = useState(
    yearOptions.includes(typeof defaultYear === 'string' ? parseInt(defaultYear.replace('Year ', '')) : defaultYear)
      ? (typeof defaultYear === 'string' ? defaultYear.replace('Year ', '') : defaultYear.toString())
      : yearOptions[0].toString()
  );
  const [selectedSubject, setSelectedSubject] = useState(defaultSubject);
  const [selectedTerm, setSelectedTerm] = useState('Term 1');
  const [selectedWeek, setSelectedWeek] = useState('1');
  const [selectedDuration, setSelectedDuration] = useState('');
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0); // Track which day is selected (0 = Monday)

  // Get subject data
  const subjectData = primaryCurriculumData[selectedSubject];
  const yearData = subjectData?.[`year${selectedYear}`];
  const termData = yearData?.terms?.find(t => t.term === selectedTerm);
  const weekData = termData?.scheme?.find(w => w.week === parseInt(selectedWeek));

  // Subject display names
  const subjectNames = {
    english: 'Language & Communication',
    mathematics: 'Mathematics',
    science: 'Science'
  };

  // Get duration based on year level (default) or user selection
  const getLessonDuration = (year) => {
    if (selectedDuration) return `${selectedDuration} min`;
    const y = parseInt(year);
    if (y <= 2) return '30 min';
    if (y <= 4) return '40 min';
    return '45 min';
  };

  // Generate full week lesson plans for all 5 days
  const generateFullWeekLessons = (week, subject, year) => {
    if (!week) return [];
    
    const topicLower = week.topic.toLowerCase();
    const strandLower = week.strand?.toLowerCase() || '';
    const duration = getLessonDuration(year);
    const yearNum = parseInt(year);
    
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const lessons = [];

    // Subject-specific content generators
    const getSubjectContent = (dayIndex) => {
      const dayFocus = ['Introduction', 'Practice', 'Reinforcement', 'Application', 'Review & Assessment'][dayIndex];
      
      if (subject === 'english') {
        if (strandLower.includes('reading') || topicLower.includes('reading') || topicLower.includes('alphabet') || topicLower.includes('letter')) {
          return getReadingContent(dayIndex, yearNum, week);
        } else if (strandLower.includes('writing') || topicLower.includes('writing') || topicLower.includes('handwriting')) {
          return getWritingContent(dayIndex, yearNum, week);
        } else if (strandLower.includes('speaking') || strandLower.includes('listening')) {
          return getSpeakingListeningContent(dayIndex, yearNum, week);
        } else {
          return getGenericEnglishContent(dayIndex, yearNum, week);
        }
      } else if (subject === 'mathematics') {
        if (topicLower.includes('count') || topicLower.includes('number') || strandLower.includes('number')) {
          return getNumberContent(dayIndex, yearNum, week);
        } else if (topicLower.includes('shape') || strandLower.includes('geometry')) {
          return getGeometryContent(dayIndex, yearNum, week);
        } else if (topicLower.includes('measure') || strandLower.includes('measurement')) {
          return getMeasurementContent(dayIndex, yearNum, week);
        } else {
          return getGenericMathContent(dayIndex, yearNum, week);
        }
      } else if (subject === 'science') {
        if (strandLower.includes('living') || topicLower.includes('animal') || topicLower.includes('plant')) {
          return getLivingThingsContent(dayIndex, yearNum, week);
        } else if (strandLower.includes('matter') || topicLower.includes('material') || topicLower.includes('water')) {
          return getMatterContent(dayIndex, yearNum, week);
        } else if (strandLower.includes('energy') || topicLower.includes('light') || topicLower.includes('sound')) {
          return getEnergyContent(dayIndex, yearNum, week);
        } else {
          return getGenericScienceContent(dayIndex, yearNum, week);
        }
      }
      return getGenericContent(dayIndex, yearNum, week, subject);
    };

    // Generate lesson for each day
    days.forEach((day, index) => {
      const content = getSubjectContent(index);
      lessons.push({
        day: day,
        title: content.title,
        duration: duration,
        focus: content.focus,
        objectives: content.objectives,
        introduction: content.introduction,
        activities: content.activities,
        materials: content.materials,
        assessment: content.assessment,
        homework: content.homework
      });
    });

    return lessons;
  };

  // Reading content for each day
  const getReadingContent = (dayIndex, yearNum, week) => {
    const contents = [
      // Monday - Introduction
      {
        title: `${week.topic} - Introduction`,
        focus: 'Introduction & Exploration',
        objectives: yearNum <= 2 
          ? ['Recognize letters and sounds', 'Listen to stories with attention']
          : ['Identify key vocabulary', 'Make predictions about text'],
        introduction: yearNum <= 2 
          ? `Today we start learning about ${week.topic}! Let's discover new letters and words together.`
          : `Today we begin ${week.topic}. Good readers preview text before reading.`,
        activities: [
          { name: 'Warm-up Song', duration: '5 min', description: yearNum <= 2 ? 'Sing alphabet song with actions' : 'Vocabulary game - word of the day' },
          { name: 'Story Introduction', duration: '10 min', description: 'Teacher reads aloud, students listen and follow' },
          { name: 'Letter/Word Hunt', duration: yearNum <= 2 ? '10 min' : '15 min', description: yearNum <= 2 ? 'Find letters in the story' : 'Find key vocabulary in text' },
          { name: 'Discussion', duration: '5 min', description: 'Talk about what we learned' }
        ],
        materials: yearNum <= 2 ? 'Alphabet chart, picture books, flashcards' : 'Reading books, vocabulary cards, whiteboard',
        assessment: 'Observe participation and letter/word recognition',
        homework: yearNum <= 2 ? 'Practice letter sounds at home' : 'Read one page and tell family about it'
      },
      // Tuesday - Practice
      {
        title: `${week.topic} - Guided Practice`,
        focus: 'Guided Practice',
        objectives: yearNum <= 2 
          ? ['Practice letter formation', 'Sound out simple words']
          : ['Read text fluently', 'Answer comprehension questions'],
        introduction: `Yesterday we started ${week.topic}. Today we will practice together.`,
        activities: [
          { name: 'Review', duration: '5 min', description: 'Quick review of yesterday\'s lesson' },
          { name: 'Guided Reading', duration: '15 min', description: yearNum <= 2 ? 'Teacher leads reading, students follow' : 'Small group guided reading' },
          { name: 'Partner Practice', duration: '10 min', description: 'Read with a partner' },
          { name: 'Recording', duration: '5 min', description: yearNum <= 2 ? 'Trace letters/words' : 'Answer questions in book' }
        ],
        materials: 'Reading books, worksheets, pencils',
        assessment: 'Check reading accuracy and comprehension',
        homework: yearNum <= 2 ? 'Read with family member' : 'Read 5 minutes silently'
      },
      // Wednesday - Reinforcement
      {
        title: `${week.topic} - Skills Building`,
        focus: 'Reinforcement',
        objectives: yearNum <= 2 
          ? ['Match sounds to letters', 'Read simple words']
          : ['Identify story elements', 'Summarize what was read'],
        introduction: `Let's keep building our ${week.topic} skills today!`,
        activities: [
          { name: 'Phonics Game', duration: '10 min', description: yearNum <= 2 ? 'Sound matching game' : 'Word building activity' },
          { name: 'Independent Reading', duration: '10 min', description: 'Students read at their level' },
          { name: 'Comprehension Activity', duration: '10 min', description: yearNum <= 2 ? 'Picture matching' : 'Story map or summary' },
          { name: 'Share Time', duration: '5 min', description: 'Share favorite part' }
        ],
        materials: 'Books, game cards, worksheets',
        assessment: 'Monitor reading progress',
        homework: 'Draw favorite character or scene'
      },
      // Thursday - Application
      {
        title: `${week.topic} - Application`,
        focus: 'Application',
        objectives: yearNum <= 2 
          ? ['Use letters in writing', 'Read independently']
          : ['Connect reading to real life', 'Express opinions about text'],
        introduction: `Today we use what we learned about ${week.topic} in new ways!`,
        activities: [
          { name: 'Reading Stations', duration: '15 min', description: 'Rotate through reading activities' },
          { name: 'Creative Response', duration: '10 min', description: yearNum <= 2 ? 'Draw and label' : 'Write a response to reading' },
          { name: 'Peer Share', duration: '5 min', description: 'Share work with partner' },
          { name: 'Class Discussion', duration: '5 min', description: 'Connect to real life' }
        ],
        materials: 'Station materials, paper, crayons, pencils',
        assessment: 'Review creative responses',
        homework: 'Tell family 3 things you learned'
      },
      // Friday - Review & Assessment
      {
        title: `${week.topic} - Review & Assessment`,
        focus: 'Review & Assessment',
        objectives: ['Demonstrate learning from the week', 'Celebrate progress'],
        introduction: `It's Friday! Let's show what we learned about ${week.topic} this week.`,
        activities: [
          { name: 'Week Review', duration: '10 min', description: 'Review key concepts from the week' },
          { name: 'Assessment Activity', duration: '10 min', description: yearNum <= 2 ? 'Letter/word recognition check' : 'Reading comprehension quiz' },
          { name: 'Celebration', duration: '5 min', description: 'Reading awards and recognition' },
          { name: 'Next Week Preview', duration: '5 min', description: 'Preview what\'s coming next' }
        ],
        materials: 'Assessment sheets, stickers, certificates',
        assessment: 'Formal assessment of week\'s learning',
        homework: 'Read something you enjoy over the weekend'
      }
    ];
    return contents[dayIndex];
  };

  // Writing content for each day
  const getWritingContent = (dayIndex, yearNum, week) => {
    const contents = [
      {
        title: `${week.topic} - Introduction`,
        focus: 'Introduction & Modeling',
        objectives: yearNum <= 2 
          ? ['Hold pencil correctly', 'Form letters from top to bottom']
          : ['Understand writing purpose', 'Plan writing ideas'],
        introduction: `Writing helps us share our ideas! Today we learn about ${week.topic}.`,
        activities: [
          { name: 'Finger Exercises', duration: '5 min', description: 'Warm up hands and fingers' },
          { name: 'Teacher Modeling', duration: '10 min', description: 'Teacher demonstrates writing skill' },
          { name: 'Air Writing', duration: '5 min', description: 'Practice in the air together' },
          { name: 'First Try', duration: '10 min', description: 'Students try on paper with guidance' }
        ],
        materials: 'Paper, pencils, whiteboard, chart paper',
        assessment: 'Observe pencil grip and formation',
        homework: 'Practice writing name 3 times'
      },
      {
        title: `${week.topic} - Practice`,
        focus: 'Guided Practice',
        objectives: yearNum <= 2 
          ? ['Write letters on lines', 'Copy simple words']
          : ['Write complete sentences', 'Use punctuation correctly'],
        introduction: `Let's practice our ${week.topic} writing today!`,
        activities: [
          { name: 'Quick Review', duration: '5 min', description: 'Review yesterday\'s skill' },
          { name: 'Guided Writing', duration: '15 min', description: 'Write together step by step' },
          { name: 'Partner Check', duration: '5 min', description: 'Check each other\'s work' },
          { name: 'More Practice', duration: '10 min', description: 'Independent writing time' }
        ],
        materials: 'Lined paper, pencils, erasers',
        assessment: 'Check letter formation and spacing',
        homework: 'Write 5 words neatly'
      },
      {
        title: `${week.topic} - Building Skills`,
        focus: 'Skill Building',
        objectives: yearNum <= 2 
          ? ['Write words with spaces', 'Write from left to right']
          : ['Organize ideas logically', 'Use describing words'],
        introduction: `Today we build our ${week.topic} skills even more!`,
        activities: [
          { name: 'Word Building', duration: '10 min', description: 'Build words letter by letter' },
          { name: 'Sentence Writing', duration: '15 min', description: yearNum <= 2 ? 'Write simple sentences' : 'Write paragraph with support' },
          { name: 'Peer Sharing', duration: '5 min', description: 'Share writing with partner' },
          { name: 'Teacher Conference', duration: '5 min', description: 'Individual feedback' }
        ],
        materials: 'Writing books, word walls, picture prompts',
        assessment: 'Review writing for content and neatness',
        homework: 'Write about your family'
      },
      {
        title: `${week.topic} - Creative Writing`,
        focus: 'Application',
        objectives: yearNum <= 2 
          ? ['Write with pictures', 'Share writing with others']
          : ['Write for different purposes', 'Edit own writing'],
        introduction: `Today we use our ${week.topic} skills to create something special!`,
        activities: [
          { name: 'Brainstorm', duration: '5 min', description: 'Think of writing ideas' },
          { name: 'Draft Writing', duration: '15 min', description: 'Write first draft' },
          { name: 'Illustrate', duration: '10 min', description: 'Add pictures to writing' },
          { name: 'Author\'s Chair', duration: '5 min', description: 'Share with class' }
        ],
        materials: 'Paper, pencils, crayons, drawing materials',
        assessment: 'Assess creativity and skill application',
        homework: 'Write a story or letter'
      },
      {
        title: `${week.topic} - Celebration`,
        focus: 'Review & Celebration',
        objectives: ['Show writing improvement', 'Celebrate writing achievements'],
        introduction: `It's Writing Celebration Day! Let's show off our ${week.topic} work!`,
        activities: [
          { name: 'Final Copy', duration: '10 min', description: 'Make best copy of week\'s writing' },
          { name: 'Gallery Walk', duration: '10 min', description: 'Display and view classmates\' work' },
          { name: 'Writing Awards', duration: '5 min', description: 'Recognize effort and improvement' },
          { name: 'Week Review', duration: '5 min', description: 'What did we learn?' }
        ],
        materials: 'Display materials, certificates, stickers',
        assessment: 'Portfolio review and celebration',
        homework: 'Write something fun over the weekend'
      }
    ];
    return contents[dayIndex];
  };

  // Speaking & Listening content
  const getSpeakingListeningContent = (dayIndex, yearNum, week) => {
    const contents = [
      {
        title: `${week.topic} - Introduction`,
        focus: 'Introduction',
        objectives: ['Listen attentively', 'Follow simple instructions'],
        introduction: `Today we practice being good listeners and speakers!`,
        activities: [
          { name: 'Greeting Circle', duration: '5 min', description: 'Greet each other properly' },
          { name: 'Listening Game', duration: '10 min', description: 'Simon Says or follow directions' },
          { name: 'Story Time', duration: '10 min', description: 'Listen to a story' },
          { name: 'Discussion', duration: '10 min', description: 'Answer questions about story' }
        ],
        materials: 'Story book, props, picture cards',
        assessment: 'Observe listening and response',
        homework: 'Listen to a story at home'
      },
      {
        title: `${week.topic} - Practice`,
        focus: 'Practice',
        objectives: ['Speak clearly', 'Take turns in conversation'],
        introduction: `Let's practice speaking and listening today!`,
        activities: [
          { name: 'Partner Talk', duration: '10 min', description: 'Practice conversations in pairs' },
          { name: 'Show and Tell', duration: '15 min', description: 'Students share about objects' },
          { name: 'Listening Check', duration: '5 min', description: 'Ask questions about what others said' },
          { name: 'Reflection', duration: '5 min', description: 'What makes good speaking?' }
        ],
        materials: 'Objects for show and tell, talking stick',
        assessment: 'Check speaking clarity and listening',
        homework: 'Have a conversation with family'
      },
      {
        title: `${week.topic} - Role Play`,
        focus: 'Role Play',
        objectives: ['Use appropriate language', 'Express ideas clearly'],
        introduction: `Today we pretend and practice different conversations!`,
        activities: [
          { name: 'Scenario Setup', duration: '5 min', description: 'Introduce role play situations' },
          { name: 'Practice', duration: '15 min', description: 'Practice in small groups' },
          { name: 'Performance', duration: '10 min', description: 'Groups perform for class' },
          { name: 'Feedback', duration: '5 min', description: 'Positive comments from peers' }
        ],
        materials: 'Props, costumes, picture cards',
        assessment: 'Assess communication skills',
        homework: 'Practice role play at home'
      },
      {
        title: `${week.topic} - Story Telling`,
        focus: 'Story Telling',
        objectives: ['Tell stories in sequence', 'Use expression when speaking'],
        introduction: `Everyone loves stories! Today we become storytellers!`,
        activities: [
          { name: 'Story Elements', duration: '5 min', description: 'Review beginning, middle, end' },
          { name: 'Story Planning', duration: '10 min', description: 'Plan a simple story' },
          { name: 'Story Telling', duration: '15 min', description: 'Tell stories to partners/class' },
          { name: 'Listening Response', duration: '5 min', description: 'Respond to stories heard' }
        ],
        materials: 'Story cards, puppets, props',
        assessment: 'Assess story structure and delivery',
        homework: 'Tell a story to family'
      },
      {
        title: `${week.topic} - Review`,
        focus: 'Review & Assessment',
        objectives: ['Demonstrate speaking skills', 'Show listening comprehension'],
        introduction: `Let's celebrate our speaking and listening skills!`,
        activities: [
          { name: 'Skills Review', duration: '10 min', description: 'Review week\'s skills' },
          { name: 'Assessment', duration: '10 min', description: 'Speaking/listening task' },
          { name: 'Awards', duration: '5 min', description: 'Recognize good communicators' },
          { name: 'Preview', duration: '5 min', description: 'Next week\'s topics' }
        ],
        materials: 'Assessment checklist, certificates',
        assessment: 'Formal assessment of skills',
        homework: 'Practice good listening all weekend'
      }
    ];
    return contents[dayIndex];
  };

  // Generic English content fallback
  const getGenericEnglishContent = (dayIndex, yearNum, week) => {
    const focuses = ['Introduction', 'Guided Practice', 'Reinforcement', 'Application', 'Review'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Develop language skills', 'Participate in activities'],
      introduction: `Today we continue learning about ${week.topic}.`,
      activities: [
        { name: 'Warm-up', duration: '5 min', description: 'Review and activate prior knowledge' },
        { name: 'Main Activity', duration: '15 min', description: `Focus on ${week.topic}` },
        { name: 'Practice', duration: '10 min', description: 'Guided and independent practice' },
        { name: 'Wrap-up', duration: '5 min', description: 'Review and preview' }
      ],
      materials: 'Textbook, worksheets, teaching aids',
      assessment: 'Observe participation and understanding',
      homework: `Practice ${week.topic} skills`
    };
  };

  // Number/Math content
  const getNumberContent = (dayIndex, yearNum, week) => {
    const contents = [
      {
        title: `${week.topic} - Introduction`,
        focus: 'Introduction & Concrete',
        objectives: yearNum <= 2 
          ? ['Count objects accurately', 'Recognize numbers']
          : ['Understand number relationships', 'Apply operations'],
        introduction: `Numbers are everywhere! Let's explore ${week.topic} today.`,
        activities: [
          { name: 'Counting Song', duration: '5 min', description: yearNum <= 2 ? 'Count to 20 song' : 'Skip counting chant' },
          { name: 'Hands-on Activity', duration: '15 min', description: 'Use counters and manipulatives' },
          { name: 'Recording', duration: '10 min', description: 'Write numbers and count' },
          { name: 'Math Talk', duration: '5 min', description: 'Discuss number patterns' }
        ],
        materials: 'Counters, number cards, ten frames, worksheets',
        assessment: 'Check counting accuracy',
        homework: 'Count objects at home'
      },
      {
        title: `${week.topic} - Practice`,
        focus: 'Guided Practice',
        objectives: yearNum <= 2 
          ? ['Write numbers correctly', 'Compare quantities']
          : ['Solve problems accurately', 'Show work clearly'],
        introduction: `Let's practice our ${week.topic} skills together!`,
        activities: [
          { name: 'Review Game', duration: '5 min', description: 'Quick number facts game' },
          { name: 'Guided Problems', duration: '15 min', description: 'Work through problems together' },
          { name: 'Partner Practice', duration: '10 min', description: 'Solve problems with a partner' },
          { name: 'Check Work', duration: '5 min', description: 'Review answers together' }
        ],
        materials: 'Worksheets, manipulatives, whiteboard',
        assessment: 'Check problem-solving accuracy',
        homework: 'Complete 5 math problems'
      },
      {
        title: `${week.topic} - Games`,
        focus: 'Practice with Games',
        objectives: ['Apply skills in games', 'Build math fluency'],
        introduction: `Math games help us learn! Let's play with ${week.topic}!`,
        activities: [
          { name: 'Math Stations', duration: '20 min', description: 'Rotate through math games' },
          { name: 'Number Challenge', duration: '10 min', description: 'Friendly math competition' },
          { name: 'Recording', duration: '5 min', description: 'Write what we practiced' }
        ],
        materials: 'Game materials, dice, cards, counters',
        assessment: 'Observe strategy and accuracy',
        homework: 'Play a number game at home'
      },
      {
        title: `${week.topic} - Word Problems`,
        focus: 'Application',
        objectives: ['Solve word problems', 'Explain thinking'],
        introduction: `Math helps us solve real problems! Let's use ${week.topic}!`,
        activities: [
          { name: 'Problem Reading', duration: '5 min', description: 'Read and understand problems' },
          { name: 'Problem Solving', duration: '15 min', description: 'Solve word problems step by step' },
          { name: 'Share Strategies', duration: '10 min', description: 'Explain how we solved problems' },
          { name: 'More Practice', duration: '5 min', description: 'Independent problem solving' }
        ],
        materials: 'Word problem cards, drawing paper, manipulatives',
        assessment: 'Check problem-solving process',
        homework: 'Solve a problem from real life'
      },
      {
        title: `${week.topic} - Assessment`,
        focus: 'Review & Assessment',
        objectives: ['Show understanding of concepts', 'Demonstrate skills learned'],
        introduction: `Let's show what we learned about ${week.topic} this week!`,
        activities: [
          { name: 'Quick Review', duration: '5 min', description: 'Review key concepts' },
          { name: 'Assessment', duration: '15 min', description: 'Math quiz or test' },
          { name: 'Math Awards', duration: '5 min', description: 'Celebrate math achievements' },
          { name: 'Preview', duration: '5 min', description: 'Next week\'s topics' }
        ],
        materials: 'Assessment papers, pencils, stickers',
        assessment: 'Formal assessment',
        homework: 'Practice math facts all weekend'
      }
    ];
    return contents[dayIndex];
  };

  // Geometry content
  const getGeometryContent = (dayIndex, yearNum, week) => {
    const focuses = ['Explore Shapes', 'Shape Properties', 'Building & Creating', 'Real-World Shapes', 'Assessment'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Identify shapes', 'Describe shape properties'],
      introduction: `Shapes are all around us! Let's discover ${week.topic}!`,
      activities: [
        { name: 'Shape Hunt', duration: '5 min', description: 'Find shapes in classroom' },
        { name: 'Shape Activity', duration: '15 min', description: dayIndex === 0 ? 'Explore with pattern blocks' : dayIndex === 2 ? 'Build with shapes' : 'Shape sorting and matching' },
        { name: 'Drawing/Recording', duration: '10 min', description: 'Draw and label shapes' },
        { name: 'Share', duration: '5 min', description: 'Share discoveries' }
      ],
      materials: 'Pattern blocks, shape cards, drawing paper',
      assessment: 'Check shape recognition and description',
      homework: 'Draw 3 shapes found at home'
    };
  };

  // Measurement content
  const getMeasurementContent = (dayIndex, yearNum, week) => {
    const focuses = ['Introduction', 'Non-standard Units', 'Standard Units', 'Comparing', 'Assessment'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Measure objects', 'Compare measurements'],
      introduction: `Measurement helps us describe things! Let's measure!`,
      activities: [
        { name: 'Comparing', duration: '5 min', description: 'Compare two objects' },
        { name: 'Measuring', duration: '15 min', description: 'Measure objects around classroom' },
        { name: 'Recording', duration: '10 min', description: 'Record measurements' },
        { name: 'Discussion', duration: '5 min', description: 'Share results' }
      ],
      materials: 'Rulers, measuring tape, objects to measure',
      assessment: 'Check measuring accuracy',
      homework: 'Measure something at home'
    };
  };

  // Generic Math fallback
  const getGenericMathContent = (dayIndex, yearNum, week) => {
    const focuses = ['Introduction', 'Practice', 'Games', 'Application', 'Assessment'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Understand math concepts', 'Solve problems accurately'],
      introduction: `Math is problem-solving! Let's work on ${week.topic}!`,
      activities: [
        { name: 'Warm-up', duration: '5 min', description: 'Mental math or number facts' },
        { name: 'Main Lesson', duration: '15 min', description: `Teach ${week.topic} concepts` },
        { name: 'Practice', duration: '10 min', description: 'Guided and independent practice' },
        { name: 'Review', duration: '5 min', description: 'Check understanding' }
      ],
      materials: 'Math materials, worksheets, manipulatives',
      assessment: 'Check work for accuracy',
      homework: 'Practice math problems'
    };
  };

  // Science: Living Things content
  const getLivingThingsContent = (dayIndex, yearNum, week) => {
    const contents = [
      {
        title: `${week.topic} - Observation`,
        focus: 'Observation',
        objectives: ['Observe living things carefully', 'Identify characteristics'],
        introduction: `Scientists observe carefully! Let's look at ${week.topic}!`,
        activities: [
          { name: 'Wonder', duration: '5 min', description: 'What do we wonder about?' },
          { name: 'Observation', duration: '15 min', description: 'Observe pictures or specimens' },
          { name: 'Recording', duration: '10 min', description: 'Draw what we see' },
          { name: 'Sharing', duration: '5 min', description: 'Share observations' }
        ],
        materials: 'Pictures, specimens, magnifying glasses, science journals',
        assessment: 'Check observation details',
        homework: 'Observe a living thing at home'
      },
      {
        title: `${week.topic} - Investigation`,
        focus: 'Investigation',
        objectives: ['Ask questions', 'Investigate to find answers'],
        introduction: `Today we investigate ${week.topic} like real scientists!`,
        activities: [
          { name: 'Questions', duration: '5 min', description: 'What do we want to know?' },
          { name: 'Investigation', duration: '20 min', description: 'Hands-on investigation' },
          { name: 'Discussion', duration: '10 min', description: 'What did we discover?' }
        ],
        materials: 'Investigation materials, charts',
        assessment: 'Observe investigation skills',
        homework: 'Find out more about today\'s topic'
      },
      {
        title: `${week.topic} - Comparison`,
        focus: 'Compare & Classify',
        objectives: ['Compare living things', 'Sort into groups'],
        introduction: `Let's compare and sort ${week.topic}!`,
        activities: [
          { name: 'Review', duration: '5 min', description: 'Remember what we learned' },
          { name: 'Sorting Activity', duration: '15 min', description: 'Sort living things by features' },
          { name: 'Venn Diagram', duration: '10 min', description: 'Compare two things' },
          { name: 'Share', duration: '5 min', description: 'Explain our sorting' }
        ],
        materials: 'Sorting cards, Venn diagram, pictures',
        assessment: 'Check classification reasoning',
        homework: 'Sort things at home'
      },
      {
        title: `${week.topic} - Environment`,
        focus: 'Habitat & Needs',
        objectives: ['Understand where things live', 'Identify basic needs'],
        introduction: `Living things need certain things to survive! Let's explore!`,
        activities: [
          { name: 'Introduction', duration: '5 min', description: 'What do living things need?' },
          { name: 'Habitat Activity', duration: '15 min', description: 'Match organisms to habitats' },
          { name: 'Create Habitat', duration: '10 min', description: 'Draw or build a habitat' },
          { name: 'Share', duration: '5 min', description: 'Present our habitats' }
        ],
        materials: 'Habitat pictures, art supplies',
        assessment: 'Check understanding of needs',
        homework: 'Look at habitats around your home'
      },
      {
        title: `${week.topic} - Review`,
        focus: 'Review & Assessment',
        objectives: ['Demonstrate learning', 'Share what we know'],
        introduction: `Let's show what we learned about ${week.topic}!`,
        activities: [
          { name: 'Review Game', duration: '10 min', description: 'Fun review activity' },
          { name: 'Assessment', duration: '10 min', description: 'Show what we know' },
          { name: 'Sharing', duration: '10 min', description: 'Share projects or drawings' },
          { name: 'Preview', duration: '5 min', description: 'What\'s next' }
        ],
        materials: 'Assessment materials, student work',
        assessment: 'Formal assessment',
        homework: 'Explore nature this weekend'
      }
    ];
    return contents[dayIndex];
  };

  // Science: Matter content
  const getMatterContent = (dayIndex, yearNum, week) => {
    const focuses = ['Explore', 'Investigate', 'Sort & Classify', 'Changes', 'Assessment'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Explore properties of matter', 'Describe observations'],
      introduction: `Everything is made of matter! Let's explore ${week.topic}!`,
      activities: [
        { name: 'Wonder', duration: '5 min', description: 'What do we know?' },
        { name: 'Investigation', duration: '15 min', description: 'Hands-on exploration' },
        { name: 'Recording', duration: '10 min', description: 'Record observations' },
        { name: 'Discussion', duration: '5 min', description: 'Share discoveries' }
      ],
      materials: 'Various materials, containers, science journals',
      assessment: 'Check observation and description skills',
      homework: 'Find different materials at home'
    };
  };

  // Science: Energy content
  const getEnergyContent = (dayIndex, yearNum, week) => {
    const focuses = ['Explore', 'Investigate', 'Sources', 'Uses', 'Assessment'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Understand energy', 'Identify energy sources'],
      introduction: `Energy makes things happen! Let's explore ${week.topic}!`,
      activities: [
        { name: 'Experience', duration: '5 min', description: 'Experience the energy' },
        { name: 'Investigation', duration: '15 min', description: 'Explore energy in action' },
        { name: 'Recording', duration: '10 min', description: 'Draw or write observations' },
        { name: 'Discussion', duration: '5 min', description: 'Share what we learned' }
      ],
      materials: 'Energy sources, experiment materials',
      assessment: 'Check understanding of energy',
      homework: 'Find energy at home'
    };
  };

  // Generic Science fallback
  const getGenericScienceContent = (dayIndex, yearNum, week) => {
    const focuses = ['Question', 'Investigate', 'Observe', 'Record', 'Conclude'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Think like a scientist', 'Record observations'],
      introduction: `Scientists are curious! Let's explore ${week.topic}!`,
      activities: [
        { name: 'Wonder', duration: '5 min', description: 'Ask questions' },
        { name: 'Investigate', duration: '15 min', description: 'Hands-on science' },
        { name: 'Record', duration: '10 min', description: 'Draw and write' },
        { name: 'Share', duration: '5 min', description: 'Discuss findings' }
      ],
      materials: 'Science materials, journals',
      assessment: 'Observe scientific thinking',
      homework: 'Be curious about the world'
    };
  };

  // Generic content fallback
  const getGenericContent = (dayIndex, yearNum, week, subject) => {
    const focuses = ['Introduction', 'Practice', 'Application', 'Extension', 'Review'];
    return {
      title: `${week.topic} - ${focuses[dayIndex]}`,
      focus: focuses[dayIndex],
      objectives: ['Learn about the topic', 'Participate actively'],
      introduction: `Today we continue learning about ${week.topic}!`,
      activities: [
        { name: 'Opening', duration: '5 min', description: 'Review and introduction' },
        { name: 'Main Activity', duration: '15 min', description: 'Key learning activity' },
        { name: 'Practice', duration: '10 min', description: 'Student practice time' },
        { name: 'Closing', duration: '5 min', description: 'Summary and preview' }
      ],
      materials: 'Teaching materials, worksheets',
      assessment: 'Check understanding',
      homework: 'Practice at home'
    };
  };

  const generatePlan = () => {
    if (!weekData) return;

    const days = generateFullWeekLessons(weekData, selectedSubject, selectedYear);
    const plan = {
      year: selectedYear,
      subject: selectedSubject,
      subjectName: subjectNames[selectedSubject],
      term: selectedTerm,
      week: selectedWeek,
      weekData: weekData,
      days: days
    };
    setGeneratedPlan(plan);
    setSelectedDay(0); // Reset to Monday when generating new plan
  };

  const printPlan = () => {
    if (!generatedPlan) return;

    const printWindow = window.open('', '_blank');
    const { weekData, days, subjectName } = generatedPlan;

    const subjectColors = {
      english: { primary: '#059669', light: '#d1fae5', dark: '#065f46' },
      mathematics: { primary: '#2563eb', light: '#dbeafe', dark: '#1e40af' },
      science: { primary: '#7c3aed', light: '#ede9fe', dark: '#5b21b6' }
    };
    const colors = subjectColors[selectedSubject];

    const dayHtml = days.map((day, index) => `
      <div class="day-section ${index > 0 ? 'page-break' : ''}">
        <div class="day-header ${index === 0 ? 'sample' : 'template'}">
          <div class="day-badge">${day.day}</div>
          <div class="day-title">
            ${index === 0 ? `<span class="sample-badge">📋 SAMPLE LESSON</span>` : `<span class="template-badge">✏️ TEMPLATE - Fill in your details</span>`}
          </div>
        </div>
        
        <table class="lesson-table">
          <tr>
            <th width="20%">Lesson Title</th>
            <td>${day.title}</td>
          </tr>
          <tr>
            <th>Duration</th>
            <td>${day.duration}</td>
          </tr>
          <tr>
            <th>Learning Objectives</th>
            <td>
              <ul>
                ${day.objectives.map(obj => `<li>${obj}</li>`).join('')}
              </ul>
            </td>
          </tr>
          <tr>
            <th>Introduction</th>
            <td class="intro-cell">${day.introduction}</td>
          </tr>
          <tr>
            <th>Main Activities</th>
            <td>
              <table class="activities-table">
                <tr><th>Activity</th><th>Time</th><th>Description</th></tr>
                ${day.activities.map(act => `
                  <tr>
                    <td>${act.name}</td>
                    <td>${act.duration}</td>
                    <td>${act.description}</td>
                  </tr>
                `).join('')}
              </table>
            </td>
          </tr>
          <tr>
            <th>Materials</th>
            <td>${day.materials}</td>
          </tr>
          <tr>
            <th>Assessment</th>
            <td>${day.assessment}</td>
          </tr>
          <tr>
            <th>Homework</th>
            <td>${day.homework}</td>
          </tr>
        </table>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Weekly Lesson Plan - Year ${generatedPlan.year} ${subjectName}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; }
          
          .header { 
            background: linear-gradient(135deg, ${colors.primary}, ${colors.dark}); 
            color: white; 
            padding: 20px; 
            border-radius: 12px; 
            margin-bottom: 25px;
          }
          .header h1 { margin: 0 0 5px 0; font-size: 22px; }
          .header-info { display: flex; gap: 15px; margin-top: 15px; flex-wrap: wrap; }
          .header-badge { background: rgba(255,255,255,0.2); padding: 5px 12px; border-radius: 15px; font-size: 13px; }
          
          .week-overview {
            background: ${colors.light};
            border: 2px solid ${colors.primary};
            border-radius: 10px;
            padding: 15px;
            margin-bottom: 25px;
          }
          .week-overview h3 { margin: 0 0 10px 0; color: ${colors.dark}; }
          .outcomes-list { margin: 10px 0; padding-left: 20px; }
          .outcomes-list li { margin: 5px 0; }
          
          .day-section { margin-bottom: 30px; }
          .page-break { page-break-before: always; }
          
          .day-header { 
            display: flex; 
            align-items: center; 
            gap: 15px; 
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 3px solid ${colors.primary};
          }
          .day-header.sample { border-bottom-color: #059669; }
          .day-header.template { border-bottom-color: #d97706; }
          
          .day-badge {
            background: ${colors.primary};
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 14px;
          }
          .day-header.sample .day-badge { background: #059669; }
          .day-header.template .day-badge { background: #d97706; }
          
          .sample-badge { 
            background: #d1fae5; 
            color: #065f46; 
            padding: 4px 10px; 
            border-radius: 5px; 
            font-size: 12px;
            font-weight: bold;
          }
          .template-badge { 
            background: #fef3c7; 
            color: #92400e; 
            padding: 4px 10px; 
            border-radius: 5px; 
            font-size: 12px;
            font-weight: bold;
          }
          
          .lesson-table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 13px;
          }
          .lesson-table th, .lesson-table td { 
            border: 1px solid #d1d5db; 
            padding: 10px; 
            text-align: left; 
            vertical-align: top;
          }
          .lesson-table th { 
            background: #f3f4f6; 
            font-weight: bold;
            width: 20%;
          }
          .lesson-table ul { margin: 5px 0; padding-left: 20px; }
          .lesson-table li { margin: 3px 0; }
          
          .activities-table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 12px;
          }
          .activities-table th, .activities-table td { 
            border: 1px solid #e5e7eb; 
            padding: 6px; 
          }
          .activities-table th { background: ${colors.light}; }
          
          .intro-cell { font-style: italic; line-height: 1.5; }
          
          .teacher-notes {
            background: #fef3c7;
            border: 2px dashed #d97706;
            border-radius: 8px;
            padding: 15px;
            margin-top: 20px;
          }
          .teacher-notes h4 { margin: 0 0 10px 0; color: #92400e; }
          
          .footer {
            text-align: center;
            padding: 20px;
            background: #f8fafc;
            border-radius: 8px;
            margin-top: 30px;
            font-size: 12px;
            color: #64748b;
          }
          
          @media print {
            .page-break { page-break-before: always; }
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📚 Weekly Lesson Plan</h1>
          <p>Year ${generatedPlan.year} ${subjectName}</p>
          <div class="header-info">
            <span class="header-badge">📅 ${generatedPlan.term} - Week ${generatedPlan.week}</span>
            <span class="header-badge">📖 ${weekData.strand}</span>
          </div>
        </div>
        
        <div class="week-overview">
          <h3>📋 Week Topic: ${weekData.topic}</h3>
          <p><strong>Strand:</strong> ${weekData.strand}</p>
          <p><strong>Learning Outcomes for this week:</strong></p>
          <ul class="outcomes-list">
            ${weekData.learningOutcomes?.map(lo => `<li>${lo}</li>`).join('') || '<li>See curriculum guide</li>'}
          </ul>
          <p><strong>Resources:</strong> ${weekData.resources?.join(', ') || 'Textbooks, worksheets, teaching aids'}</p>
        </div>
        
        <div class="teacher-notes">
          <h4>📝 Instructions for Teachers</h4>
          <p><strong>Monday</strong> is provided as a <strong>SAMPLE lesson</strong> - you can use it as-is or modify it.</p>
          <p><strong>Tuesday to Friday</strong> are <strong>TEMPLATES</strong> - fill in your own activities based on:</p>
          <ul>
            <li>The week's learning outcomes listed above</li>
            <li>Your students' needs and abilities</li>
            <li>Available resources at your school</li>
          </ul>
        </div>
        
        ${dayHtml}
        
        <div class="footer">
          <p>🌴 Penama Education Portal - Primary Curriculum | Generated ${new Date().toLocaleDateString()}</p>
          <p>Teacher: _________________________ | Signature: _________________________</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Get available weeks based on scheme data
  const availableWeeks = termData?.scheme?.map(w => w.week) || [1,2,3,4,5,6,7,8,9,10];

  return (
    <Card className="border-2 border-green-200" data-testid="primary-lesson-generator">
      <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
        <CardTitle className="flex items-center gap-2">
          <PenTool className="w-5 h-5 text-green-600" />
          Lesson Plan Generator
        </CardTitle>
        <CardDescription>
          Generate complete weekly lesson plans for all 5 days (Monday - Friday)
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Selection Controls */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Subject</label>
            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger>
                <SelectValue placeholder="Select Subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">Language & Communication</SelectItem>
                <SelectItem value="mathematics">Mathematics</SelectItem>
                <SelectItem value="science">Science</SelectItem>
                <SelectItem value="social_science">Social Science</SelectItem>
                <SelectItem value="arts">Arts & Crafts</SelectItem>
                <SelectItem value="health_pe">Health & Physical Education</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Year Level</label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger>
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Term</label>
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger>
                <SelectValue placeholder="Select Term" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Term 1">Term 1</SelectItem>
                <SelectItem value="Term 2">Term 2</SelectItem>
                <SelectItem value="Term 3">Term 3</SelectItem>
                <SelectItem value="Term 4">Term 4</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Week</label>
            <Select value={selectedWeek} onValueChange={setSelectedWeek}>
              <SelectTrigger>
                <SelectValue placeholder="Select Week" />
              </SelectTrigger>
              <SelectContent>
                {availableWeeks.map(w => (
                  <SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Duration</label>
            <Select value={selectedDuration || getLessonDuration(selectedYear).replace(' min', '')} onValueChange={setSelectedDuration}>
              <SelectTrigger data-testid="select-primary-duration">
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({length: 28}, (_, i) => 15 + i * 5).map(min => (
                  <SelectItem key={min} value={min.toString()}>{min} min</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button 
              onClick={generatePlan}
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={!weekData}
              data-testid="generate-plan-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Plan
            </Button>
          </div>
        </div>
        
        {/* Week Preview */}
        {weekData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-green-600" />
              Week {selectedWeek}: {weekData.topic}
            </h4>
            <p className="text-sm text-gray-600 mt-1">{weekData.strand}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {weekData.learningOutcomes?.slice(0, 3).map((lo, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  <Target className="w-3 h-3 mr-1" />
                  {lo.length > 50 ? lo.substring(0, 50) + '...' : lo}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Generated Plan Preview */}
        {generatedPlan && (
          <div className="border-2 border-green-200 rounded-lg overflow-hidden" data-testid="generated-plan">
            <div className="bg-green-50 p-4 flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-green-800">
                  ✅ Weekly Lesson Plan Generated!
                </h4>
                <p className="text-sm text-green-600">
                  Year {generatedPlan.year} {generatedPlan.subjectName} | {generatedPlan.term} | Week {generatedPlan.week}
                </p>
              </div>
              <Button onClick={printPlan} className="bg-green-600 hover:bg-green-700" data-testid="print-plan-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print All Days
              </Button>
            </div>
            
            <div className="p-4">
              {/* Day Selection Tabs - NOW CLICKABLE */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                {generatedPlan.days.map((day, i) => (
                  <button 
                    key={day.day}
                    onClick={() => setSelectedDay(i)}
                    data-testid={`day-tab-${day.day.toLowerCase()}`}
                    className={`p-3 rounded-lg text-center transition-all cursor-pointer ${
                      selectedDay === i
                        ? 'bg-green-600 text-white border-2 border-green-700 shadow-md transform scale-105'
                        : 'bg-gray-100 hover:bg-gray-200 border-2 border-gray-300'
                    }`}
                  >
                    <div className={`font-bold text-sm ${selectedDay === i ? 'text-white' : 'text-gray-700'}`}>
                      {day.day}
                    </div>
                    <div className={`text-xs mt-1 ${selectedDay === i ? 'text-green-100' : 'text-gray-500'}`}>
                      {day.focus}
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Selected Day Detailed Content */}
              {generatedPlan.days[selectedDay] && (
                <div className="bg-white border-2 border-green-200 rounded-lg p-4" data-testid="day-content">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-green-800">
                        {generatedPlan.days[selectedDay].day}: {generatedPlan.days[selectedDay].title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="bg-green-50">
                          <Clock className="w-3 h-3 mr-1" />
                          {generatedPlan.days[selectedDay].duration}
                        </Badge>
                        <Badge variant="outline" className="bg-blue-50">
                          <Target className="w-3 h-3 mr-1" />
                          {generatedPlan.days[selectedDay].focus}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Learning Objectives */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-blue-600" />
                      Learning Objectives
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 bg-blue-50 p-3 rounded-lg">
                      {generatedPlan.days[selectedDay].objectives.map((obj, idx) => (
                        <li key={idx}>{obj}</li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Introduction */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-yellow-600" />
                      Introduction
                    </h4>
                    <p className="text-gray-600 bg-yellow-50 p-3 rounded-lg italic">
                      &ldquo;{generatedPlan.days[selectedDay].introduction}&rdquo;
                    </p>
                  </div>
                  
                  {/* Activities Table */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      Lesson Activities
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-purple-100">
                            <th className="border border-purple-200 px-3 py-2 text-left">Activity</th>
                            <th className="border border-purple-200 px-3 py-2 text-left w-20">Time</th>
                            <th className="border border-purple-200 px-3 py-2 text-left">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generatedPlan.days[selectedDay].activities.map((act, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="border border-gray-200 px-3 py-2 font-medium">{act.name}</td>
                              <td className="border border-gray-200 px-3 py-2 text-center">{act.duration}</td>
                              <td className="border border-gray-200 px-3 py-2">{act.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Materials, Assessment, Homework */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h5 className="font-semibold text-gray-700 flex items-center gap-1 mb-1">
                        <BookOpen className="w-4 h-4 text-gray-600" />
                        Materials
                      </h5>
                      <p className="text-sm text-gray-600">{generatedPlan.days[selectedDay].materials}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <h5 className="font-semibold text-gray-700 flex items-center gap-1 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Assessment
                      </h5>
                      <p className="text-sm text-gray-600">{generatedPlan.days[selectedDay].assessment}</p>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <h5 className="font-semibold text-gray-700 flex items-center gap-1 mb-1">
                        <Home className="w-4 h-4 text-orange-600" />
                        Homework
                      </h5>
                      <p className="text-sm text-gray-600">{generatedPlan.days[selectedDay].homework}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>💡 Tip:</strong> Click on any day tab above to view its detailed lesson plan. 
                  All 5 days have complete lesson content based on the week&apos;s topic: &ldquo;{generatedPlan.weekData?.topic}&rdquo;.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {!weekData && (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Select Subject, Year, Term, and Week to generate a lesson plan</p>
            <p className="text-sm mt-2">
              {!termData?.scheme?.length && selectedTerm !== 'Term 1' && yearData 
                ? `Note: ${selectedTerm} scheme data is being developed. Try Term 1.` 
                : ''}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrimaryLessonPlanGenerator;
